import { Stack, Typography } from '@mui/material'
import { C1BreadcrumbLinkProps } from 'c1ui'
import { AppAvatar } from 'components/core/avatar'
import { ResourceTypeAvatar } from 'components/pages/entitlement/resource-type/icons'
import { routes } from 'lib/routes'
import { FormattedMessage } from 'react-intl'

/**
 * Breadcrumb definitions for the application.
 *
 * Static crumbs use routes.* directly.
 * Dynamic crumbs (functions) use route builder functions which
 * automatically handle URL encoding internally.
 */
export const crumbs = {
    '/admin/applications': {
        text: <FormattedMessage id="components_app_crumbs_applications_crumb" defaultMessage="Applications" />,
        href: routes.applications.list,
    },
    '/admin/applications/[app_id]': (appId: string, appName: string): C1BreadcrumbLinkProps => ({
        href: routes.applications.detail(appId),
        text: <Stack direction="row" spacing={1} alignItems="center">
            <AppAvatar appId={appId} appName={appName} size="xs" />
            <Typography>{appName}</Typography>
        </Stack>,
    }),
    '/admin/applications/[app_id]/[resource_type]': (appID: string, resourceType?: string): C1BreadcrumbLinkProps | false => {
        let text: React.ReactElement
        let slug: string

        if (!resourceType) return false

        if (resourceType?.includes('group')) {
            text = <FormattedMessage id="components_app_resources_list_crumb_groups" defaultMessage="Groups" />
            slug = 'groups'
        } else if (resourceType?.includes('role')) {
            text = <FormattedMessage id="components_app_resources_list_crumb_roles" defaultMessage="Roles" />
            slug = 'roles'
        } else {
            text = <FormattedMessage id="components_app_resources_list_crumb_resources" defaultMessage="Resources" />
            slug = 'resources'
        }

        return {
            href: routes.applications.detailTab(appID, slug),
            text,
        }
    },
    '/admin/applications/[app_id]/resources/[resource_type_id]': (
        appID: string,
        resourceID: string,
        resourceName: string,
        resourceTypeID: string,
        resourceType?: string,
    ): C1BreadcrumbLinkProps => ({
        href: routes.applications.resource(appID, resourceTypeID, resourceID),
        text: <Stack direction="row" spacing={1} alignItems="center">
            <ResourceTypeAvatar resourceTypeName={resourceType || ''} size="xs" />
            <Typography>{resourceName}</Typography>
        </Stack>,
    }),
    '/admin/applications/[app_id]/accounts': (appId: string): C1BreadcrumbLinkProps => ({
        href: routes.applications.accounts(appId),
        text: <FormattedMessage id="components_appuser_crumb" defaultMessage="Accounts" />,
    }),
    '/admin/applications/[app_id]/group-templates': (appId: string): C1BreadcrumbLinkProps => ({
        href: routes.applications.groupTemplates.list(appId),
        text: <FormattedMessage id="components_group_templates_crumb" defaultMessage="Group templates" />,
    }),
    '/assignments/reviews': {
        href: routes.assignments.reviews.list,
        text: <FormattedMessage id="pages_reviews_crumb" defaultMessage="Access reviews" />,
    },
    '/password-reset': {
        href: routes.passwordReset.index,
        text: <FormattedMessage id="pages_password_reset_crumb" defaultMessage="Password reset" />,
    },
    '/requests': {
        href: routes.requests.list,
        text: <FormattedMessage id="pages_requests_crumb" defaultMessage="Requests" />,
    },
    '/admin/applications/shadow-apps': {
        href: routes.applications.shadowApps,
        text: <FormattedMessage id="shadow_apps_crumb" defaultMessage="Shadow apps" />,
    },
    '/admin/directory/users': {
        href: routes.users.list,
        text: <FormattedMessage id="pages_users_crumb" defaultMessage="Users" />,
    },
    '/admin/campaigns': {
        href: routes.campaigns.list,
        text: <FormattedMessage id="pages_access_reviews_crumb" defaultMessage="Campaigns" />,
    },
    '/admin/campaigns/templates': {
        href: routes.campaigns.templates.list,
        text: <FormattedMessage id="pages_access_reviews_recurring_crumb" defaultMessage="Templates" />,
    },
    '/admin/access-profiles': {
        href: routes.accessProfiles.list,
        text: <FormattedMessage id="pages_request_catalogs_crumb" defaultMessage="Access profiles" />,
    },
    '/admin/task-log': {
        href: routes.taskLog,
        text: <FormattedMessage id="pages_tasks_crumb" defaultMessage="All tasks" />,
    },
    '/admin/settings/external-ticketing': {
        href: routes.settings.externalTicketing.list,
        text: <FormattedMessage id="pages_external_ticketing_integrations_crumb" defaultMessage="ITSM integrations" />,
    },
    '/admin/settings/service-desk-integration': {
        href: routes.settings.serviceDeskIntegration.list,
        text: <FormattedMessage id="pages_service_desk_integrations_crumb" defaultMessage="Service desk integrations" />,
    },
    '/admin/settings/external-data-sources': {
        href: routes.settings.externalDataSources,
        text: <FormattedMessage id="pages_external_data_sources_crumb" defaultMessage="External data sources" />,
    },
    '/admin/conflict-monitors': {
        href: routes.conflictMonitors.list,
        text: <FormattedMessage id="pages_conflict_monitors_crumb" defaultMessage="Conflict monitors" />,
    },
    '/admin/connectors': {
        href: routes.connectors.list,
        text: <FormattedMessage id="pages_connectors_crumb" defaultMessage="Connectors" />,
    },
    '/admin/directory/groups': {
        href: routes.directory.groups.list,
        text: <FormattedMessage id="pages_dynamic_groups_crumb" defaultMessage="Groups" />,
    },
    '/admin/settings/policies': {
        href: routes.settings.policies.list,
        text: <FormattedMessage id="pages_policies_crumb" defaultMessage="Policies" />,
    },
    '/admin/offboarding': {
        href: routes.offboarding.list,
        text: <FormattedMessage id="pages_offboarding_crumb" defaultMessage="Offboarding" />,
    },
    '/admin/settings/vaults': {
        href: routes.settings.vaults.list,
        text: <FormattedMessage id="pages_vaults_crumb" defaultMessage="Vaults" />,
    },
    '/admin/automations': {
        text: <FormattedMessage id="automations" defaultMessage="Automations" />,
        href: routes.automations.list,
    },
    '/admin/directory/user-data-sources': {
        text: <FormattedMessage id="user-data-sources" defaultMessage="Data sources" />,
        href: routes.userDataSources.list,
    },
    '/admin/directory/user-data-sources/profile-types': {
        text: <FormattedMessage id="profile-types" defaultMessage="Profile types" />,
        href: routes.userDataSources.profileTypes.list,
    },
    '/admin/directory/user-data-sources/pushset': {
        text: <FormattedMessage id="pushset" defaultMessage="Push set" />,
        href: routes.userDataSources.pushset.list,
    },
    '/admin/directory/user-data-sources/push-rule': {
        text: <FormattedMessage id="push-rule" defaultMessage="Push rule" />,
        href: routes.userDataSources.pushRule,
    },
    '/admin/settings/passwords': {
        text: <FormattedMessage id="passwords" defaultMessage="Passwords" />,
        href: routes.settings.passwords.list,
    },
    '/admin/settings/agentic-ai': {
        text: <FormattedMessage id="agentic_ai" defaultMessage="Agentic AI" />,
        href: routes.settings.agenticAi.list,
    },
}
